package com.evoapps.evoshift.ui.reports

import kotlin.math.round

/**
 * SH-207: sizes a printed report's type so that its tables fit one A4 page. The HTML is laid out
 * by the platform's print engine, so the height is estimated from the same numbers the report's
 * CSS uses (line height 1.3, the cell padding, 0.5 pt borders): each table row is its lines times
 * the line height plus the padding, and [fixedMm] covers the title, the meta line, notes,
 * signatures and footer. The largest size from [MAX_PT] down to [MIN_PT] that fits is chosen; a
 * report too long even at [MIN_PT] keeps that size and continues on the next page (nobody is left
 * out).
 */
object PrintFit {
    /** A4 portrait (297 mm) less the 10 mm page margins. */
    const val PAGE_MM = 277.0

    /**
     * SH-251: the height the reports are fitted to. The print engines differ from the estimate
     * (the iOS print sheet lays the page out with its own margins and system fonts, and may
     * choose US Letter, 279 mm), so about 8% of the page is kept in reserve: on the iPad the
     * footer of a 31-day month went over to a second page at [PAGE_MM].
     */
    const val FIT_MM = 255.0
    const val MAX_PT = 9.0
    const val MIN_PT = 6.5
    private const val STEP_PT = 0.25
    private const val PT_MM = 25.4 / 72
    private const val LINE_HEIGHT = 1.3
    private const val BORDER_MM = 0.18

    data class Fit(val fontPt: Double, val padMm: Double, val heightMm: Double, val fits: Boolean) {
        /** The CSS variables the report's stylesheet reads (--fs, --pad). */
        val style: String get() = "--fs:${fontPt}pt;--pad:${padMm}mm"
    }

    /** Vertical cell padding at [fontPt]: about a ninth of the size, as at 9 pt (1 mm). */
    fun padMm(fontPt: Double): Double = round(fontPt * 0.11 * 100) / 100

    /** Height of the tables' rows ([rowLines] lines each) and the fixed parts at [fontPt]. */
    fun heightMm(rowLines: List<Int>, fixedMm: Double, fontPt: Double): Double {
        val line = fontPt * LINE_HEIGHT * PT_MM
        val pad = 2 * padMm(fontPt) + BORDER_MM
        return fixedMm + rowLines.sumOf { maxOf(1, it) * line + pad }
    }

    fun fit(rowLines: List<Int>, fixedMm: Double, availableMm: Double = FIT_MM): Fit = fit(fixedMm, availableMm) { rowLines }

    /** SH-258: the same where the rows' lines depend on the size ([rowLinesAt]: a smaller type fits more on a line). */
    fun fit(fixedMm: Double, availableMm: Double = FIT_MM, rowLinesAt: (Double) -> List<Int>): Fit {
        var size = MAX_PT
        while (size > MIN_PT && heightMm(rowLinesAt(size), fixedMm, size) > availableMm) size -= STEP_PT
        val height = heightMm(rowLinesAt(size), fixedMm, size)
        return Fit(size, padMm(size), height, height <= availableMm)
    }
}
